import { join, resolve } from 'node:path';
import type { VariantOf } from 'variant';
import { variant, constant, fields, payload, isType } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import { pathExists } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { DevCenterClient, CliAppTemplate } from '@wix/dev-center-client';
import { validateDirectory, validatePackageName } from './validations/index.js';
import type { TemplateParams } from './validations/index.js';

export interface CommandOptions {
  template?: string;
  templateRepo?: string;
  templateRepoPath?: string;
  templatePath?: string;
  skipInstall: boolean;
  skipGit: boolean;
  templateParams?: TemplateParams;
  appName?: string;
  extendAppId?: string;
}

interface Clients {
  devCenterClient: DevCenterClient;
}

export const TemplateSource = variant({
  App: payload<CliAppTemplate>(),
  GitRepo: fields<{ url: string; path?: string }>(),
  Local: fields<{ path: string }>(),
  Default: constant({ path: resolve(__dirname, '../templates/app/blank') }),
});

export type TemplateSource = VariantOf<typeof TemplateSource>;

async function parseTemplateOptions(
  options: CommandOptions,
  { devCenterClient }: Clients
) {
  if (options.template) {
    const appTemplate = await devCenterClient.getCliAppTemplate(
      options.template
    );

    if (!appTemplate) {
      throw new CliError({
        code: CliErrorCode.CliAppTemplateNotFound({ id: options.template }),
        cause: null,
      });
    }

    return TemplateSource.App(appTemplate);
  }

  if (options.templateRepo) {
    return TemplateSource.GitRepo({
      url: options.templateRepo,
      path: options.templateRepoPath,
    });
  }

  if (options.templatePath) {
    if (!(await pathExists(options.templatePath))) {
      throw new CliError({
        code: CliErrorCode.LocalTemplatePathNotFound({
          path: options.templatePath,
        }),
        cause: null,
      });
    }

    return TemplateSource.Local({ path: options.templatePath });
  }
}

export const AppNameOptions = variant({
  Provided: fields<{ appName: string; packageName: string }>(),
  Missing: {},
});

export type AppNameOptions = VariantOf<typeof AppNameOptions>;

async function parseAppNameOptions(
  targetParentFolder: string,
  options: CommandOptions
) {
  const appName = options.appName;
  if (appName) {
    const packageName = kebabCase(appName);
    if (!(await validateDirectory(join(targetParentFolder, packageName)))) {
      throw new CliError({
        code: CliErrorCode.AppNameArgumentIsInvalid({
          errorMessage: `"${packageName}" directory is not empty`,
        }),
        cause: undefined,
      });
    }

    const npmValidationErrors = await validatePackageName(packageName);
    if (isType(npmValidationErrors, 'Error')) {
      throw new CliError({
        code: CliErrorCode.AppNameArgumentIsInvalid({
          errorMessage: `Invalid package name "${packageName}". Fix the following issues: ${npmValidationErrors.payload.join(', ')}`,
        }),
        cause: undefined,
      });
    }

    return AppNameOptions.Provided({ appName, packageName });
  }

  return AppNameOptions.Missing();
}

export async function parseCommandOptions(
  targetParentFolder: string,
  options: CommandOptions,
  clients: Clients
) {
  let template: TemplateSource | undefined = await parseTemplateOptions(
    options,
    clients
  );
  const appNameOptions = await parseAppNameOptions(targetParentFolder, options);

  if (!template && isType(appNameOptions, 'Provided')) {
    template = TemplateSource.Default();
  }

  return { template, appName: appNameOptions };
}
